import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MapPin, Loader2, Search, Image as ImageIcon, ShieldCheck, AlertTriangle } from 'lucide-react';
import { PropertyProfilePanel } from '@/components/property/PropertyProfilePanel';
import { PropertyHazardsPanel } from '@/components/property/PropertyHazardsPanel';
import { RebuildEstimatePanel } from '@/components/property/RebuildEstimatePanel';
import { ResidentialQuotePanel } from '@/components/property/ResidentialQuotePanel';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A', bg: '#F5F7FA' };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const confidenceStyle = c => c === 'high' ? { background: 'rgba(29,158,117,0.12)', color: C.green }
  : c === 'medium' ? { background: 'rgba(178,117,22,0.12)', color: C.amber }
    : { background: 'rgba(226,75,74,0.1)', color: C.red };

const Fact = ({ k, v, testid }) => (
  <div className="flex justify-between gap-3 text-[11px] border-t border-[rgba(136,150,168,0.1)] py-1" data-testid={testid}>
    <span style={{ color: C.slate }}>{k}</span>
    <span className="font-mono text-right break-all" style={{ color: C.navy }}>{v === null || v === undefined || v === '' ? '—' : String(v)}</span>
  </div>
);

const ImagePane = ({ title, url, meta, testid }) => (
  <div className="rounded-sm overflow-hidden border border-[rgba(136,150,168,0.2)]" data-testid={testid}>
    <div className="px-3 py-1.5 flex items-center justify-between" style={{ background: C.bg }}>
      <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.navy }}>{title}</span>
      <span className="text-[9px]" style={{ color: C.slate }}>{meta?.source}{meta?.licence ? ` · ${meta.licence}` : ''}</span>
    </div>
    {url ? <img src={`${BACKEND}${url}`} alt={title} className="w-full h-44 object-cover" />
      : <div className="h-44 flex items-center justify-center text-[11px]" style={{ color: C.slate }}>{meta?.note || meta?.source || 'not available'}</div>}
  </div>
);

export const PropertyAddressCard = ({ api }) => {
  const [q, setQ] = useState('');
  const [sugg, setSugg] = useState(null);
  const [addr, setAddr] = useState(null);
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sources, setSources] = useState(null);
  const [profile, setProfile] = useState(null);
  const [typed, setTyped] = useState(null);
  const timer = useRef(null);

  useEffect(() => { api.get('/property/sources').then(({ data }) => setSources(data)).catch(() => {}); }, [api]);

  const suggest = text => {
    clearTimeout(timer.current);
    if (text.trim().length < 3) { setSugg(null); return; }
    timer.current = setTimeout(async () => {
      try { const { data } = await api.get('/property/address/suggest', { params: { q: text } }); setSugg(data); }
      catch (err) { console.error('API error:', err?.message); }
    }, 350);
  };

  const resolve = async text => {
    setBusy(true); setError(null); setAddr(null); setImg(null); setSugg(null); setProfile(null); setTyped(null);
    try {
      const { data } = await api.get('/property/address/resolve', { params: { q: text } });
      setAddr(data);
      const im = await api.get(`/property/${encodeURIComponent(data.pid)}/imagery`);
      setImg(im.data);
    } catch (err) { setError(err?.response?.data?.detail || err?.message || 'resolve failed'); }
    setBusy(false);
  };

  return (
    <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="property-address-card" style={{ borderLeft: `3px solid ${C.blue}` }}>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2" style={{ color: C.navy }}>
            <MapPin className="w-4 h-4" style={{ color: C.blue }} /> Property address resolver
          </h3>
          <p className="text-xs mt-0.5" style={{ color: C.slate }}>
            Family 6 · M1 address + imagery · M2 profile, hazards, rebuild · M3 questions, options, taxes — Australian addresses resolve on the state registers (NSW · VIC · QLD · TAS); every field is sourced or says why it is not.
          </p>
        </div>
        <span className="flex items-center gap-2">
          {typed !== null && (
            <span className="text-[10px] font-semibold px-2 py-1 rounded" style={{ background: 'rgba(26,76,124,0.1)', color: C.blue }} data-testid="property-typed-counter">
              typed inputs: {typed} · budget ≤ 20 on free data
            </span>
          )}
          <span className="text-[10px] font-semibold px-2 py-1 rounded uppercase" style={{ background: C.bg, color: C.navy }} data-testid="property-mode-chip">
            {sources?.mode || 'DEMO · free data'}{sources && !sources.geoscape?.configured ? ' · Geoscape key pending' : ''}
          </span>
        </span>
      </div>

      <div className="relative">
        <div className="flex gap-2">
          <Input value={q} onChange={e => { setQ(e.target.value); suggest(e.target.value); }}
            onKeyDown={e => e.key === 'Enter' && q.trim() && resolve(q)}
            placeholder="e.g. 84 Macquarie St Dubbo NSW 2830" className="rounded-sm text-sm" data-testid="property-address-input" />
          <Button onClick={() => resolve(q)} disabled={busy || q.trim().length < 3} className="rounded-sm" style={{ background: C.blue }} data-testid="property-resolve-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-1.5 text-xs">Resolve</span>
          </Button>
        </div>
        {sugg?.suggestions?.length > 0 && (
          <ul className="absolute z-20 left-0 right-16 mt-1 bg-white border border-[rgba(136,150,168,0.25)] rounded-sm shadow-lg max-h-56 overflow-auto" data-testid="property-suggestions">
            {sugg.suggestions.map(s => (
              <li key={s.pid}>
                <button type="button" onClick={() => { setQ(s.label); resolve(s.label); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-[#F5F7FA] flex justify-between gap-2" data-testid={`property-suggestion-${s.pid}`}>
                  <span style={{ color: C.navy }}>{s.label}</span>
                  <span className="font-mono text-[10px] shrink-0" style={{ color: C.slate }}>{s.pid}</span>
                </button>
              </li>
            ))}
            <li className="px-3 py-1 text-[9px]" style={{ color: C.slate }}>{sugg.elapsed_ms} ms · {sugg.suggestions[0]?.source}</li>
          </ul>
        )}
      </div>

      {error && (
        <p className="mt-3 text-xs flex items-center gap-1.5" style={{ color: C.red }} data-testid="property-resolve-error">
          <AlertTriangle className="w-3.5 h-3.5" /> {error}
        </p>
      )}

      {addr && (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2" data-testid="property-resolved">
            <p className="text-sm font-semibold" style={{ color: C.navy }} data-testid="property-resolved-label">{addr.label}</p>
            <p className="mt-1 flex flex-wrap gap-1.5 items-center">
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: C.bg, color: C.navy }} data-testid="property-pid">{addr.pid}</span>
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase" style={confidenceStyle(addr.confidence)} data-testid="property-confidence">{addr.confidence}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: C.bg, color: C.slate }}>{addr.pid_kind.replace('_', ' ')}</span>
              {addr.cached && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: C.bg, color: C.slate }}>cached</span>}
            </p>
            <div className="mt-3">
              <Fact k="Source" v={addr.source} testid="property-fact-source" />
              <Fact k="Licence" v={addr.licence} />
              <Fact k="Retrieved" v={addr.retrieved_at?.slice(0, 19).replace('T', ' ') + 'Z'} />
              <Fact k="Lat / lng" v={`${addr.lat?.toFixed(6)}, ${addr.lng?.toFixed(6)}`} testid="property-fact-latlng" />
              <Fact k="Locality · LGA" v={[addr.locality, addr.lga].filter(Boolean).join(' · ')} />
              <Fact k="Mesh block" v={addr.mesh_block} />
              <Fact k={`Register id (${addr.register?.kind})`} v={addr.register?.id} />
              {Object.entries(addr.extras || {}).map(([k, v]) => <Fact key={k} k={k.replace(/_/g, ' ')} v={v} />)}
              <Fact k="G-NAF PID" v={addr.gnaf_pid || 'pending Geoscape key'} testid="property-fact-gnaf" />
            </div>
            <p className="mt-2 text-[10px] flex items-center gap-1" style={{ color: C.green }}>
              <ShieldCheck className="w-3 h-3" /> {addr.elapsed_ms ? `${addr.elapsed_ms} ms on the register` : 'served from the PID cache'}
            </p>
          </div>
          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <ImagePane title="Aerial" url={img?.aerial_url} meta={img?.aerial} testid="property-aerial" />
            <ImagePane title="Map" url={img?.static_map_url} meta={img?.static_map} testid="property-map" />
            <div className="md:col-span-2 text-[10px] flex items-center gap-1.5 flex-wrap" style={{ color: C.slate }} data-testid="property-imagery-note">
              <ImageIcon className="w-3 h-3" />
              {img ? <>Signed links expire in {Math.round(img.expires_in_seconds / 60)} min · no key in the browser · Street View: {img.street_view?.note}{img.note ? ` · ${img.note}` : ''}</> : 'loading imagery…'}
            </div>
          </div>
        </div>
      )}

      {addr && (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="property-m2-panels">
          <PropertyProfilePanel api={api} pid={addr.pid} onProfile={setProfile} />
          <PropertyHazardsPanel api={api} pid={addr.pid} />
          <div className="lg:col-span-2">
            <RebuildEstimatePanel api={api} pid={addr.pid} profile={profile} onEstimate={r => setTyped(r.typed_inputs)} />
          </div>
          <div className="lg:col-span-2" data-testid="property-m3-panel">
            <ResidentialQuotePanel api={api} pid={addr.pid} />
          </div>
        </div>
      )}
    </Card>
  );
};
